/**
 * SignaturePad — canvas-based drawn signature capture.
 *
 * Exposes onChange(dataUrl) — returns a PNG base64 data URL when the user
 * lifts their pointer (mouse, touch, or pen). Empty string when cleared.
 *
 * Works on touch + desktop. ~150 LOC, no external deps.
 */
import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

export default function SignaturePad({ onChange, width = 480, height = 160 }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  const getCtx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    const ctx = c.getContext("2d");
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2;
    return ctx;
  };

  const fillBackground = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
  };

  useEffect(() => {
    // Adjust for high-DPI screens so the line stays crisp
    const c = canvasRef.current;
    if (!c) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    c.width = width * ratio;
    c.height = height * ratio;
    c.style.width = `${width}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    fillBackground();
  }, [width, height]);

  const pointerPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = getCtx();
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = getCtx();
    const { x, y } = pointerPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    const c = canvasRef.current;
    if (c && hasInk) {
      onChange?.(c.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    fillBackground();
    setHasInk(false);
    onChange?.("");
  };

  return (
    <div className="space-y-2" data-testid="signature-pad">
      <div className="relative rounded-lg border-2 border-dashed border-clinical-border bg-white">
        <canvas
          ref={canvasRef}
          className="block touch-none cursor-crosshair rounded-lg"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid="signature-canvas"
        />
        {!hasInk && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-clinical-subtext">
            Sign here
          </span>
        )}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 text-xs text-clinical-subtext hover:text-clinical-text"
          data-testid="signature-clear"
        >
          <Eraser size={12} /> Clear
        </button>
      </div>
    </div>
  );
}
